import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { GetApp } from '@material-ui/icons';
import axios from 'axios';

import Page from '../pages/Page.js';
import useSettings from "../settings/useSettings.js";
import RenaiEventCommitment from "../hacks/RenaiEventCommitment.js";

const HEX_SIZE = 60;
const DIAGRAM_DIM = HEX_SIZE*7.5;
const PADDING = 5;
const HORIZONTAL_ORIENTATION = 0, VERTICAL_ORIENTATION = 1;

const H_OFFSET = [];
H_OFFSET[HORIZONTAL_ORIENTATION] = (3 / 2) * HEX_SIZE + PADDING;
H_OFFSET[VERTICAL_ORIENTATION] = Math.sqrt(3) * HEX_SIZE + PADDING;

const V_OFFSET = [];
V_OFFSET[HORIZONTAL_ORIENTATION] = Math.sqrt(3) * HEX_SIZE + PADDING;
V_OFFSET[VERTICAL_ORIENTATION] = (3 / 2) * HEX_SIZE + PADDING;

//offsets are defined in hex dimensions (1 = full dimension)
const defaultConfId = 'default';
const configurations = {
    [defaultConfId]: {
        "orientation": HORIZONTAL_ORIENTATION,
        "start": [0.75, 1.1],
        "text": {
            width: "50%",
            height: "50%",
            top: "35%",
            left: "50%"
        },
        "offset": [
            { dx: 0, dy: 0, id: "Assurance" },
            { dx: 1, dy: 0.5, id: "Assessment" },
            { dx: 2, dy: 0, id: "Success" },
            { dx: 3, dy: -0.5, id: "Capability" },
            { dx: 2, dy: 1, id: "Employability" },
            { dx: 2, dy: 2, id: "Design" }
        ]
    },
    "Assurance": {
        "orientation": HORIZONTAL_ORIENTATION,
        "start": [1, 1],
        "text": {
            width: "50%",
            height: "50%",
            top: "10%",
            left: "50%"
        },
        "offset": [
            { dx: 0, dy: 0, id: "Assurance" },
            { dx: 1, dy: 0.5, id: "Assessment" },
            { dx: 0, dy: 2, id: "Success" },
            { dx: 2, dy: 1, id: "Capability" },
            { dx: 1, dy: 1.5, id: "Employability" },
            { dx: 2, dy: 2, id: "Design" }
        ]
    },
    "Assessment": {
        "orientation": HORIZONTAL_ORIENTATION,
        "start": [1.2, 1],
        "text": {
            width: "50%",
            height: "40%",
            top: "70%",
            left: "40%"
        },
        "offset": [
            { dx: 0, dy: 0, id: "Assurance" },
            { dx: 1, dy: 0.5, id: "Assessment" },
            { dx: 0, dy: 1, id: "Success" },
            { dx: 2, dy: 1, id: "Capability" },
            { dx: 1, dy: 1.5, id: "Employability" },
            { dx: 0, dy: 2, id: "Design" }
        ]
    },
    "Success": {
        "orientation": HORIZONTAL_ORIENTATION,
        "start": [1, 1],
        "text": {
            width: "50%",
            height: "50%",
            top: "65%",
            left: "38%"
        },
        "offset": [
            { dx: 0, dy: 0, id: "Assurance" },
            { dx: 1, dy: 0.5, id: "Assessment" },
            { dx: 2, dy: 0, id: "Success" },
            { dx: 3, dy: 0.5, id: "Capability" },
            { dx: 2, dy: 1, id: "Employability" },
            { dx: 1, dy: 1.5, id: "Design" }
        ]
    },
    "Capability": {
        "orientation": VERTICAL_ORIENTATION,
        "start": [1.2, 2],
        "text": {
            width: "50%",
            height: "50%",
            top: "10%",
            left: "40%"
        },
        "offset": [
            { dx: 0, dy: 0, id: "Assurance" },
            { dx: -0.5, dy: 1, id: "Assessment" },
            { dx: 0.5, dy: 1, id: "Success" },
            { dx: 1.5, dy: 1, id: "Capability" },
            { dx: 0, dy: 2, id: "Employability" },
            { dx: 1, dy: 2, id: "Design" }
        ]
    },
    "Employability": {
        "orientation": VERTICAL_ORIENTATION,
        "start": [1.2, 1],
        "text": {
            width: "50%",
            height: "80%",
            top: "30%",
            left: "50%"
        },
        "offset": [
            { dx: 0, dy: 0, id: "Assurance" },
            { dx: 1, dy: 0, id: "Assessment" },
            { dx: 0.5, dy: 1, id: "Success" },
            { dx: 0, dy: 2, id: "Capability" },
            { dx: -0.5, dy: 3, id: "Employability" },
            { dx: 0.5, dy: 3, id: "Design" }
        ]
    },
    "Design": {
        "orientation": VERTICAL_ORIENTATION,
        "start": [1.2, 1],
        "text": {
            width: "45%",
            height: "50%",
            top: "55%",
            left: "50%"
        },
        "offset": [
            { dx: 0, dy: 0, id: "Assurance" },
            { dx: 1, dy: 0, id: "Assessment" },
            { dx: -0.5, dy: 1, id: "Success" },
            { dx: 1.5, dy: 1, id: "Capability" },
            { dx: 0, dy: 2, id: "Employability" },
            { dx: 1, dy: 2, id: "Design" }
        ]
    }
};

const useStyles = makeStyles((theme) => ({
    '@keyframes hexShimmer': {
        '0%': { filter: 'brightness(1)' },
        '5%': { filter: 'brightness(1.8)' },
        '20%': { filter: 'brightness(1)' },
    },

    '@keyframes hexShimmerClick': {
        '0%': { filter: 'brightness(1)' },
        '5%': { filter: 'brightness(1.8)' },
        '20%': { filter: 'brightness(1)' },
    },

    hexSvg: {
        width: '100%',
        '& a': {
            textDecoration: 'none !important',
        },
        '&.shimmer $hexGroup use': {
            animationName: '$hexShimmer',
        },
    },

    hexCore: {
        cursor: 'pointer',
        transition: 'fill 0.3s, transform 0.5s',
        strokeWidth: 4
    },

    hexButton: {
        '&:nth-child(1) use': { animationDelay: '0.1s' },
        '&:nth-child(2) use': { animationDelay: '0.2s' },
        '&:nth-child(3) use': { animationDelay: '0.3s' },
        '&:nth-child(4) use': { animationDelay: '0.4s' },
        '&:nth-child(5) use': { animationDelay: '0.5s' },
        '&:nth-child(6) use': { animationDelay: '0.6s' },
    },

    hexGroup: {
        transition: 'transform 0.5s ease',

        '& use': {
            color: 'var(--white)',
            transition: 'color 0.5s, filter 0.3s, stroke 0.5s',
            animationDuration: '5s',
            animationIterationCount: 'infinite',
            animationName: 'none',
            stroke: 'var(--darkBlue)',
        },

        '&:hover use': {
            color: 'var(--brightBlue)',
        },

        '& text': {
            fill: 'var(--darkBlue)',
            fontSize: '12px',
            textAnchor: 'middle',
            pointerEvents: 'none',
            transform: 'translateY(6px)',
            transition: 'fill 0.5s ease',
        },
    },
    hexActive: {
        '& use': {
            color: 'var(--brightBlue) !important',
        },
        '& text': {
            fill: 'var(--white)'
        }
    },
    hexAttended: {
        '& use': {
            color: 'var(--darkBlue)',
        },
        '& text': {
            fill: 'var(--white)'
        }
    },
    hexComplete: {
        '& use': {
            stroke: 'url(#gold-material)',
        },
    },
    hexGroupInner: {
        transition: 'transform 0.5s ease',
        '&:hover': {
            transform: 'scale(1.08)'
        },
    },
    wrapper: {
        display: 'flex',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        },
    },
    textside: {
        flex: 1,
    },
    diagramside: {
        flex: 1,
    },
}));

function RenaiEventDiagram({ area, tags, newAttendanceTagIds, onSelectDomain }) {
    const settings = useSettings();
    const [attendances, setAttendances] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [configuration, setConfiguration] = useState(defaultConfId);
    const [completion, setCompletion] = useState(null);
    const styles = useStyles();
    const svgRef = useRef(null);

    useEffect(() => {
        setError(null);
        setLoading(true);
        const primaryCategoryId = settings.get("Renai diagram tag category");

        axios.get("api/pageattendances/" + primaryCategoryId, {}, { withCredentials: true })
            .then(response => {
                setAttendances(response.data);
                axios.get("api/renaicommitments/completion", {}, { withCredentials: true })
                    .then(response => {
                        setCompletion(response.data);
                        setLoading(false);
                    })
                    .catch(e => {
                        console.log(e);
                        if (!'response' in e)
                            setError("no access");
                        setError(e.response?.data ?? "Error");
                        setLoading(false);
                    });
            })
            .catch(e => {
                if (!'response' in e)
                    setError("no access");
                setError(e.response?.data?.errors?.reflection ?? "Error");
                setLoading(false);
            });
    }, [settings]);

    const primaryTags = useMemo(() => {
        const primaryCategoryId = settings.get("Renai diagram tag category");
        const primaryCategory = tags.find(t => t.id == primaryCategoryId);
        if (!primaryCategory)
            return [];
        const enabled = primaryCategory.options.find(o => o.enabled);
        if(enabled)
            setConfiguration(defaultConfId/*enabled.name*/);
        else
            setConfiguration(defaultConfId);
        return primaryCategory.options;
    }, [tags, settings]);

    const domainData = useMemo(() => {
        const allAttended = [...attendances, ...newAttendanceTagIds];
        const output = primaryTags.reduce((acc, t) => {
            acc[t.id] = 0;
            return acc;
        }, {});

        for (let a of allAttended) {
            if (!(a in output))
                continue;
            output[a]++;
        }
        return output;
    }, [primaryTags, newAttendanceTagIds, attendances]);

    const someDomainsAttended = useMemo(() => {
        for (let d in domainData)
            if (domainData[d] !== 0)
                return true;
        return false;
    }, [domainData]);

    const allDomainsAttended = useMemo(() => {
        for (let d in domainData)
            if (domainData[d] === 0)
                return false;
        return true;
    }, [domainData]);

    useEffect(() => {
        apply_orientation_to_hex(configurations[configuration].orientation);
        if (svgRef.current) {
            svgRef.current.classList.remove('shimmer');
            setTimeout(() => {
                svgRef.current.classList.add('shimmer');
            }, 100);
        }
    }, [configuration]);

    function clickHex(e, tag) {
        e.preventDefault();
        const primaryCategoryId = settings.get("Renai diagram tag category");
        if(primaryCategoryId)
            onSelectDomain?.(primaryCategoryId, tag.id);
        document.getElementById("RenaiEventPagePreviewGrid")?.scrollIntoView({ behavior: 'smooth' });
        setConfiguration(defaultConfId/*tag.name*/);
    }
    function calcTransform(t) {
        const c = configurations[configuration];
        const sx = c.start[0] * H_OFFSET[c.orientation], sy = c.start[1] * V_OFFSET[c.orientation];
        const o = c.offset.find(o => o.id == t.name);
        const x = sx + H_OFFSET[c.orientation] * o.dx, y = sy + V_OFFSET[c.orientation] * o.dy;
        return `translate(${x}, ${y})`;
    }

    function onSubmitCommitment(cmt) {
        setCompletion({state: 'active', content: cmt});
    }

    function apply_orientation_to_hex() {
        const points = [];
        for (let i = 0; i < 6; i++) {
            var angle_deg = 60 * i - 30 * configurations[configuration].orientation;
            var angle_rad = Math.PI / 180 * angle_deg;
            points.push(`${0 + HEX_SIZE * Math.cos(angle_rad)},${0 + HEX_SIZE * Math.sin(angle_rad)}`);
        }
        return points.join(" ");
    }

    function dev_AttendNone() {
        setAttendances([]);
        setCompletion(null);
    }
    function dev_AttendOne() {
        dev_AttendAll(true);
    }
    function dev_AttendAll(shouldBreak = false) {
        const a = [ ...attendances ];
        for (let i = 0; i < primaryTags.length; i++) {
            if (a.indexOf(primaryTags[i].id) === -1) {
                a.push(primaryTags[i].id);
                if (shouldBreak === true)
                    break;
            }
        }
        setAttendances(a);
    }
    function dev_Complete() {
        dev_AttendAll();
        setCompletion({ state: 'active', "content": "complete" });
    }

    var base = document.getElementsByTagName('base')[0].getAttribute('href');

    return <>
        <Box className={styles.wrapper}>
            <Box className={styles.textside}>
                <Page area={area} id={"setting-" + area.url + " Intro"} embed={true} />
                <RenaiEventCommitment enabled={allDomainsAttended} data={completion} onSubmitCommitment={onSubmitCommitment} /> &nbsp;
                <Button download target="_blank" variant="contained" color="primary" disabled={!someDomainsAttended} href={base + "api/pageattendances/record"}><GetApp /> Download Record</Button>
            </Box>
            <Box className={styles.diagramside}>
                <svg ref={svgRef} viewBox={`0 0 ${DIAGRAM_DIM} ${DIAGRAM_DIM}`} className={styles.hexSvg + " " + (completion ? styles.hexComplete : "")}>
                    <title>Six Domain Interactive Diagram</title>
                    <defs>
                        <polygon className={styles.hexCore} points={apply_orientation_to_hex()} id="hexShape" fill="currentColor"></polygon>
                        <linearGradient id="gold-material" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#BF953F" />
                            <stop offset="25%" stopColor="#FCF6BA" />
                            <stop offset="50%" stopColor="#B38728" />
                            <stop offset="75%" stopColor="#FBF5B7" />
                            <stop offset="100%" stopColor="#AA771C" />
                        </linearGradient>
                    </defs>
                    <rect width={DIAGRAM_DIM} height={DIAGRAM_DIM} fill="transparent"></rect>
                    <g>
                        {primaryTags.map(t => <a key={t.name} className={styles.hexButton} onClick={e => clickHex(e, t)} >
                            <g className={styles.hexGroup + ' ' + (configuration === t.name ? styles.hexActive : '') + ' ' + (domainData[t.id] > 0 ? styles.hexAttended : '')}
                                transform={calcTransform(t)}>
                                <g className={styles.hexGroupInner}>
                                    <use href="#hexShape"></use>
                                    <text className={styles.hexLabel}>{t.name}</text>
                                </g>
                            </g>
                        </a>)}
                    </g>
                </svg>
                {process.env.REACT_APP_ENV !== 'Production' ? <>
                    <Button onClick={dev_AttendNone}>clear</Button>
                    <Button onClick={dev_AttendOne}>one</Button>
                    <Button onClick={dev_AttendAll}>all</Button>
                    <Button onClick={dev_Complete}>complete</Button>
                </> : null}
            </Box>
        </Box>
    </>;
}

export default RenaiEventDiagram;